import * as adminApi from '../../services/adminApi.js';
import { fetchBranches } from '../../services/branchApi.js';

export const createAdminUsersSlice = (set, get) => ({
  // Users state
  users: [],
  branches: [],
  usersLoading: false,
  usersError: null,
  usersLoaded: false,

  // ====== USER MANAGEMENT ACTIONS ======

  /**
   * Load all users and branches
   */
  loadUsers: async () => {
    set((currentState) => ({
      admin: {
        ...currentState.admin,
        usersLoading: true,
        usersError: null
      }
    }));

    try {
      const [usersResponse, branchesResponse] = await Promise.all([
        adminApi.fetchUsers(),
        fetchBranches()
      ]);

      const branches = branchesResponse.branches || [];
      const users = (usersResponse.users || []).map((user) => ({
        ...user,
        name: user.name || user.username || '',
        email: user.email || '',
        branchId: user.branch_id ? `BR-${user.branch_id.toString().padStart(3, '0')}` : '',
        active: Boolean(user.is_active)
      }));

      set((currentState) => ({
        admin: {
          ...currentState.admin,
          users,
          branches,
          usersLoading: false,
          usersError: null,
          usersLoaded: true
        }
      }));
    } catch (error) {
      set((currentState) => ({
        admin: {
          ...currentState.admin,
          usersLoading: false,
          usersError: error.message || 'Failed to load users'
        }
      }));
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - User data (name, email, role, branchId, password)
   */
  createUser: async (userData) => {
    try {
      const response = await adminApi.createUser(userData);
      const newUser = {
        ...response.user,
        branchId: response.user.branch_id ? `BR-${response.user.branch_id.toString().padStart(3, '0')}` : '',
        active: Boolean(response.user.is_active)
      };
      set((state) => ({
        admin: {
          ...state.admin,
          users: [newUser, ...state.admin.users]
        }
      }));
      return newUser;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  },

  /**
   * Update an existing user
   * @param {number} userId - User ID
   * @param {Object} updates - Fields to update
   */
  updateUser: async (userId, updates) => {
    try {
      const response = await adminApi.updateUser(userId, updates);
      const updatedUser = {
        ...response.user,
        branchId: response.user.branch_id ? `BR-${response.user.branch_id.toString().padStart(3, '0')}` : '',
        active: Boolean(response.user.is_active)
      };
      set((state) => ({
        admin: {
          ...state.admin,
          users: state.admin.users.map((u) => u.id === userId ? updatedUser : u)
        }
      }));
      return updatedUser;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  },

  /**
   * Toggle user active status
   * @param {number} userId - User ID
   */
  toggleUserActive: async (userId) => {
    const normalizedId = Number(userId);
    const state = get();
    const user = state.admin.users.find((u) => Number(u.id) === normalizedId);
    if (!user) return;
    return state.admin.updateUser(normalizedId, { active: !user.active });
  },

  /**
   * Delete a user
   * @param {number} userId - User ID
   */
  deleteUser: async (userId) => {
    const normalizedId = Number(userId);
    try {
      await adminApi.deleteUser(normalizedId);
      set((state) => ({
        admin: {
          ...state.admin,
          users: state.admin.users.filter((u) => Number(u.id) !== normalizedId)
        }
      }));
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }
});